import { useCallback, useEffect, useRef, useState } from "react";
import { IconX } from "@tabler/icons-react";
import {
  containImageRect,
  pointInImageRect,
  regionFromPoints,
  type AdvancedBeautifyRegion,
  type ImageContentRect,
} from "./advanced-beautify-annotation-utils";

interface AdvancedBeautifyAnnotationProps {
  imageSrc: string;
  alt: string;
  regions: AdvancedBeautifyRegion[];
  drawing: boolean;
  disabled?: boolean;
  onAddRegion: (region: AdvancedBeautifyRegion) => void;
  onRemoveRegion: (region: AdvancedBeautifyRegion) => void;
}

export function AdvancedBeautifyAnnotation({
  imageSrc,
  alt,
  regions,
  drawing,
  disabled = false,
  onAddRegion,
  onRemoveRegion,
}: AdvancedBeautifyAnnotationProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragStartRef = useRef<{ x: number; y: number } | undefined>(undefined);
  const dragEndRef = useRef<{ x: number; y: number } | undefined>(undefined);
  const [imageRect, setImageRect] = useState<ImageContentRect>({ left: 0, top: 0, width: 0, height: 0 });
  const [draftRegion, setDraftRegion] = useState<AdvancedBeautifyRegion>();

  const updateLayout = useCallback(() => {
    const surface = surfaceRef.current;
    const image = imageRef.current;
    if (!surface || !image?.naturalWidth || !image.naturalHeight) return;
    setImageRect(containImageRect(surface.clientWidth, surface.clientHeight, image.naturalWidth, image.naturalHeight));
  }, []);

  useEffect(() => {
    updateLayout();
    const surface = surfaceRef.current;
    if (!surface) return;
    const observer = new ResizeObserver(updateLayout);
    observer.observe(surface);
    return () => observer.disconnect();
  }, [imageSrc, updateLayout]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image?.naturalWidth || !image.naturalHeight) return;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    for (const region of [...regions, ...(draftRegion ? [draftRegion] : [])]) {
      drawRegion(context, region, canvas.width, canvas.height, region === draftRegion);
    }
  }, [draftRegion, imageRect, regions]);

  useEffect(() => {
    if (drawing) return;
    dragStartRef.current = undefined;
    dragEndRef.current = undefined;
    setDraftRegion(undefined);
  }, [drawing]);

  const pointerPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const surface = surfaceRef.current;
    if (!surface) return undefined;
    return pointInImageRect(event.clientX, event.clientY, surface.getBoundingClientRect(), imageRect);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing || disabled) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const point = pointerPoint(event);
    if (!point) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartRef.current = point;
    dragEndRef.current = point;
    setDraftRegion(regionFromPoints(point, point, "draft", regions.length + 1));
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const start = dragStartRef.current;
    if (!start || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    const point = pointerPoint(event);
    if (!point) return;
    dragEndRef.current = point;
    setDraftRegion(regionFromPoints(start, point, "draft", regions.length + 1));
  };

  const finishPointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const start = dragStartRef.current;
    if (!start) return;
    const point = pointerPoint(event) || dragEndRef.current;
    dragStartRef.current = undefined;
    dragEndRef.current = undefined;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setDraftRegion(undefined);
    if (!point) return;
    const region = regionFromPoints(start, point, crypto.randomUUID(), regions.length + 1);
    if (region.width * imageRect.width < 8 || region.height * imageRect.height < 8) return;
    onAddRegion(region);
  };

  const cancelPointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
    dragStartRef.current = undefined;
    dragEndRef.current = undefined;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setDraftRegion(undefined);
  };

  return <div
    ref={surfaceRef}
    className={`advanced-beautify-annotation${drawing ? " drawing" : ""}`}
    aria-describedby="advanced-beautify-annotation-help"
    onDragStart={(event) => event.preventDefault()}
  >
    <img ref={imageRef} src={imageSrc} alt={alt} draggable={false} onLoad={updateLayout}/>
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ left: imageRect.left, top: imageRect.top, width: imageRect.width, height: imageRect.height }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishPointer}
      onPointerCancel={cancelPointer}
    />
    {regions.map((region) => {
      const buttonOffset = 14;
      const buttonX = Math.max(
        buttonOffset,
        Math.min(imageRect.width - buttonOffset, (region.x + region.width) * imageRect.width),
      );
      const buttonY = Math.max(
        buttonOffset,
        Math.min(imageRect.height - buttonOffset, region.y * imageRect.height),
      );
      return <button
        key={region.id}
        type="button"
        className="advanced-beautify-region-close"
        style={{ left: imageRect.left + buttonX, top: imageRect.top + buttonY }}
        disabled={disabled}
        aria-label={`删除框${region.number}`}
        title={`删除框${region.number}`}
        onClick={() => onRemoveRegion(region)}
      ><IconX size={15} stroke={3}/></button>;
    })}
  </div>;
}

function drawRegion(
  context: CanvasRenderingContext2D,
  region: AdvancedBeautifyRegion,
  width: number,
  height: number,
  draft: boolean,
) {
  const x = region.x * width;
  const y = region.y * height;
  const regionWidth = region.width * width;
  const regionHeight = region.height * height;
  const lineWidth = Math.max(5, Math.round(width * 0.004));
  const badgeRadius = Math.max(17, Math.round(width * 0.014));
  const badgeX = Math.max(badgeRadius + lineWidth, Math.min(width - badgeRadius - lineWidth, x + badgeRadius));
  const badgeY = Math.max(badgeRadius + lineWidth, Math.min(height - badgeRadius - lineWidth, y + badgeRadius));
  context.save();
  context.strokeStyle = "#ff304f";
  context.fillStyle = "#ff304f";
  context.lineWidth = lineWidth;
  context.setLineDash(draft ? [lineWidth * 2, lineWidth] : []);
  context.strokeRect(x, y, regionWidth, regionHeight);
  context.setLineDash([]);
  context.beginPath();
  context.arc(badgeX, badgeY, badgeRadius, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = "#ffffff";
  context.font = `800 ${Math.round(badgeRadius * 1.15)}px Arial, sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(String(region.number), badgeX, badgeY + 1);
  context.restore();
}
